/* eslint-disable @typescript-eslint/no-explicit-any */
// For the mask data from fetchMasks.
export interface MaskDetail {
    available: boolean;
    price: number;
    count: number;
  }
  
  export interface MaskOption {
    label: string;
    value: string;
    disabled?: boolean;
  }
  
  // For ports and lags.
  export interface Port {
    id: number;
    name: string;
    mbps_capacity: number;
    datacenter?: string;
  }
  
  export interface PortAndLagData {
    ports?: Port[];
    lags?: Port[];
  }
  
  export interface PortOption {
    label: string;
    value: string;
    mbps_capacity: number;
  }
  
  // For capacities.
  export interface CapacityOption {
    label: string;
    value: number;
  }
  
  // For connection terms.
  export interface ConnectionTermOption {
    label: string;
    value: number;
  }
  
  // --- Transformation Functions ---
  
  /**
   * Transform mask data into an array of options.
   * All mask options will be included, but disabled if not available.
   */
  export function transformMaskOptions(
    masksData: Record<string, MaskDetail> | undefined
  ): MaskOption[] {
    if (!masksData) return [];
    return Object.entries(masksData).map(([mask, details]) => ({
      label: mask,
      value: mask,
      disabled: !details.available, // disable if not available
    }));
  }
  
  /**
   * Combine and transform ports and lags data into an array of options.
   */
  export function transformPortOptions(
    data: PortAndLagData | undefined
  ): PortOption[] {
    if (!data) return [];
    const portsArray = data.ports && Array.isArray(data.ports) ? data.ports : [];
    const lagsArray = data.lags && Array.isArray(data.lags) ? data.lags : [];
    const combined = [...portsArray, ...lagsArray];
    return combined.map((item) => ({
      label: item.name,
      value: String(item.id),
      mbps_capacity: item.mbps_capacity,
    }));
  }
  
  /**
   * Transform connection capacity data into an array of options.
   * The label will include "Mbps".
   */
  export function transformCapacityOptions(
    capacitiesData: any
  ): CapacityOption[] {
    if (!capacitiesData) return [];
    return Object.keys(capacitiesData).map((key) => ({
      label: `${key} Mbps`,
      value: Number(key),
    }));
  }
  
  /**
   * Format a term key (like "1M", "12M") into a readable label.
   * Here, price is divided by 100 because the data is in cents.
   */

  // format number to US dollar
  const USDollar = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    });

  export function formatTermLabel(termKey: string, price: number): string {
    const months = parseInt(termKey.replace("M", ""), 10);
    return `${months} month${months > 1 ? "s" : ""} - ${USDollar.format(price / 100)} USD`;

  }
  /**
   * Transform the connection term pricing data for a given capacity into an array of radio options.
   */
  export function transformConnectionTermOptions(
    pricingData: any,
    selectedCapacity: number | null,
    formatLabel: (termKey: string, price: number) => string
  ): ConnectionTermOption[] {
    if (selectedCapacity === null || !pricingData[selectedCapacity]) return [];
    return Object.entries(pricingData[selectedCapacity]).map(([termKey, price]) => {
      const months = parseInt(termKey.replace("M", ""), 10);
      return {
        label: formatLabel(termKey, price as number),
        value: months,
      };
    });
  }
  